import React, { useEffect, useRef, useState } from 'react';

import { LoadingType, setLoadingState } from 'prequalificationV2/actions';
import Loader from 'components/Loader';
import BankAccounts from 'prequalificationV2/components/LoanPortal/BankAccounts';
import FundingInfoForm from 'prequalificationV2/components/LoanPortal/FundingInformation';
import { BankAccountsStatus } from 'enums/BankAccountsStatus';
import { DocumentCategory } from 'enums/DocumentCategory';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import {
  getDocumentsByCategory,
  isDocumentCategoryCompleted,
  isDocumentCategoryRequired,
} from 'prequalificationV2/utils/documentsUtils';
import UploadDocumentsCategory from 'prequalificationV2/components/LoanPortal/UploadDocuments/UploadDocumentCategory';
import Section from 'prequalificationV2/components/LoanPortal/Sections/Section';
import { DashboardStep } from 'enums/DashboardStep';
import { getApplicationInfoForDashboardThunk } from 'prequalificationV2/thunks/getApplicationInfoForDashboard';
import { BankAccount, truvApi } from 'api/TruvApi';
import Autopay from 'prequalificationV2/components/LoanPortal/Autopay';
import { setDashboardStep } from 'prequalificationV2/actions/stages';
import { setLoanPortal } from 'prequalificationV2/actions/dashboardPortal';

import styles from '../InformationTabs.module.scss';

const UPDATE_BANK_ACCOUNT_STATUS_TO_MANUAL_TIMEOUT = 5 * 60 * 1000;
const BANK_ACCOUNTS_REQUEST_INTERVAL = 10 * 1000;

const FundingInformationTab = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.loading[LoadingType.GetBankAccounts]);

  const { applicationId } = useAppSelector((state) => state.application);
  const { requiredDocuments } = useAppSelector((state) => state.documents);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [chosenBankAccount, setChosenBankAccount] = useState<BankAccount>({
    bankName: '',
    routingNumber: '',
    accountType: '',
    accountNumber: '',
  });
  const {
    fundingInformationIsRequired,
    fundingInformationComplete,
    bankAccountsStatus,
    financialAccountAggregationAttemptComplete,
    autopaySectionIsCompleted,
    autopaySectionIsRequired,
  } = useAppSelector((state) => state.dashboardPortal);
  const fundingInformationSectionRolledUp = useRef(fundingInformationComplete);

  const checkBankAccountStatusUpdate = async () => {
    const { status } = await truvApi.getBankAccountsRefreshStatus({ applicationId: applicationId! });

    return status;
  };

  const requestBankAccounts = async () => {
    try {
      const { status, availableAccounts, chosenAccount } = await truvApi.getBankAccounts({
        applicationId: applicationId!,
      });

      if (
        status === BankAccountsStatus.ReceivedPayrollProvider ||
        status === BankAccountsStatus.ReceivedFinancialAccounts
      ) {
        dispatch(
          setLoanPortal({
            bankAccounts: availableAccounts,
          }),
        );
      }

      if (status === BankAccountsStatus.Done) {
        setChosenBankAccount(chosenAccount);
      }

      if (status === BankAccountsStatus.FinancialAccounts && financialAccountAggregationAttemptComplete) {
        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      }

      dispatch(
        setLoanPortal({
          bankAccountsStatus: status,
        }),
      );

      return status;
    } catch (error) {
      dispatch(setDashboardStep(DashboardStep.NotFoundContact));
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timer;
    let timeout: NodeJS.Timeout;

    const updateBankAccounts = async () => {
      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: true,
        }),
      );

      const currentStatus = await requestBankAccounts();

      if (
        currentStatus === BankAccountsStatus.PendingPayrollProvider ||
        currentStatus === BankAccountsStatus.PendingFinancialAccounts
      ) {
        interval = setInterval(async () => {
          const refreshStatus = await checkBankAccountStatusUpdate();

          if (
            refreshStatus !== BankAccountsStatus.PendingFinancialAccounts &&
            refreshStatus !== BankAccountsStatus.PendingPayrollProvider
          ) {
            clearInterval(interval);
            clearTimeout(timeout);

            dispatch(setLoanPortal({ bankAccountsStatus: refreshStatus }));
          }
        }, BANK_ACCOUNTS_REQUEST_INTERVAL);

        timeout = setTimeout(async () => {
          clearInterval(interval);

          if (currentStatus === BankAccountsStatus.PendingPayrollProvider) {
            await truvApi.setBankAccountStatus({
              applicationId: applicationId!,
              status: BankAccountsStatus.FirstAttemptManual,
            });

            dispatch(
              setLoanPortal({
                bankAccountsStatus: BankAccountsStatus.FirstAttemptManual,
              }),
            );
          }

          if (currentStatus === BankAccountsStatus.PendingFinancialAccounts) {
            await truvApi.setBankAccountStatus({
              applicationId: applicationId!,
              status: BankAccountsStatus.FinancialAccounts,
            });

            dispatch(
              setLoanPortal({
                bankAccountsStatus: BankAccountsStatus.FinancialAccounts,
              }),
            );
          }
        }, UPDATE_BANK_ACCOUNT_STATUS_TO_MANUAL_TIMEOUT);
      }

      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: false,
        }),
      );
    };

    const requestApplication = async () => {
      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: true,
        }),
      );

      await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();

      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: false,
        }),
      );
    };

    if (fundingInformationIsRequired && bankAccountsStatus !== BankAccountsStatus.FinancialAccountsAggregationError) {
      updateBankAccounts();
    }

    if (bankAccountsStatus === BankAccountsStatus.FinancialAccountsAggregationError) {
      requestApplication();
    }

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [
    bankAccountsStatus === BankAccountsStatus.PendingFinancialAccounts,
    bankAccountsStatus === BankAccountsStatus.PendingPayrollProvider,
    bankAccountsStatus === BankAccountsStatus.Done,
  ]);

  const getAccountsPageByStatus = () => {
    if (!fundingInformationIsRequired) {
      return;
    }

    if (
      (bankAccountsStatus === BankAccountsStatus.FirstAttemptManual ||
        bankAccountsStatus === BankAccountsStatus.SecondAttemptManual ||
        bankAccountsStatus === BankAccountsStatus.ThirdAttemptManual) &&
      !!fundingInformationComplete
    ) {
      dispatch(
        setLoanPortal({
          bankAccountsStatus: BankAccountsStatus.Done,
        }),
      );
    }

    switch (bankAccountsStatus) {
      case BankAccountsStatus.Done:
        return (
          <BankAccounts
            fundingInformationSectionRolledUp={fundingInformationSectionRolledUp.current}
            chosenBankAccount={chosenBankAccount}
          />
        );
      case BankAccountsStatus.PendingPayrollProvider:
      case BankAccountsStatus.ReceivedPayrollProvider:
      case BankAccountsStatus.ReceivedFinancialAccounts:
      case BankAccountsStatus.PendingFinancialAccounts:
        return <BankAccounts fundingInformationSectionRolledUp={fundingInformationSectionRolledUp.current} />;
      case BankAccountsStatus.FirstAttemptManual:
      case BankAccountsStatus.SecondAttemptManual:
      case BankAccountsStatus.ThirdAttemptManual:
        return <FundingInfoForm />;
      case BankAccountsStatus.FinancialAccounts:
        return <FundingInfoForm />;
      case BankAccountsStatus.FinancialAccountsAggregationError:
        return <></>;
      default:
        return <FundingInfoForm />;
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className={styles.requiredInformationContainer}>
          {getAccountsPageByStatus()}
          <Section
            title={'Funding'}
            subSectionStates={[
              {
                isExist: isDocumentCategoryRequired(requiredDocuments, DocumentCategory.Funding),
                isCompleted: isDocumentCategoryCompleted(requiredDocuments, DocumentCategory.Funding),
              },
            ]}
          >
            <UploadDocumentsCategory
              documents={getDocumentsByCategory(requiredDocuments, DocumentCategory.Funding)}
              category={DocumentCategory.Funding}
            />
          </Section>
          <Section
            title={'Autopay'}
            subSectionStates={[
              {
                isExist: autopaySectionIsRequired,
                isCompleted: autopaySectionIsCompleted,
              },
            ]}
          >
            <Autopay />
          </Section>
        </div>
      )}
    </>
  );
};

export default FundingInformationTab;
